import React, { useEffect, useState, useContext } from 'react';
import {
  CircularProgress,
  Container,
  Typography,
  Accordion,
  Box,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tooltip,
  Stack,
  Button,
  Badge,
  Alert
} from '@mui/material';
import { ExpandMore, Visibility, Edit, Delete } from '@mui/icons-material';
import documentoApi from '../../api/documentoApi';
import filesApi from '../../api/filesApi';
import { useNavigate } from 'react-router-dom';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { formatDate } from '../../utils/dateFormatter';

export default function DocumentiListPageMobile() {
  const { user } = useContext(AuthContext);

  const [documenti, setDocumenti] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [mergeError, setMergeError] = useState(null);
  const [merging, setMerging] = useState(false);

  const navigate = useNavigate();
  const pageSize = 10;

  // ---------------- UTILS ----------------
  const truncateText = (text, max = 80) =>
    text && text.length > max ? text.slice(0, max) + '...' : text;

  // ---------------- FETCH ----------------
  useEffect(() => fetchDocumenti(0), []);

  const fetchDocumenti = (page) => {
    setLoading(true);

    const params = { page, size: pageSize };

    let promise;

    if (user.role === 'CONTRIBUENTE') {
      promise = documentoApi.listbycontribuente(user.contribuenteId, params);
    } else if (user.role === 'STUDIO') {
      promise = documentoApi.listbystudio(user.studioId, params);
    } else {
      promise = documentoApi.list(params);
    }

    promise
      .then(r => {
        setDocumenti(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setPage(r.data.number || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false)); // FIX
  };

  // ---------------- DELETE ----------------
  const openDeleteDialog = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };

  const closeDeleteDialog = () => {
    setSelectedId(null);
    setOpenDeleteModal(false);
  };

  const confirmDelete = async () => {
    await documentoApi.delete(selectedId);
    fetchDocumenti(page);
    closeDeleteDialog();
  };

  // ---------------- PDF ----------------
  const openPdf = async (filename) => {
    const res = await filesApi.getfile(filename, user.contribuenteId);
    const url = window.URL.createObjectURL(
      new Blob([res.data], { type: 'application/pdf' })
    );
    window.open(url, '_blank');
  };

  // ---------------- MERGE ----------------
  const toggleSelection = (id) => {
    setSelectedDocumentIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const canMerge = () => {
    if (selectedDocumentIds.length < 2) return false;

    const selectedDocs = documenti.filter(d =>
      selectedDocumentIds.includes(d.id)
    );

    const first = selectedDocs[0];

    return selectedDocs.every(d =>
      d.contribuente.id === first.contribuente.id &&
      d.tipo === first.tipo &&
      d.numeroDocumento === first.numeroDocumento
    );
  };

  const handleMerge = async () => {
    setMergeError(null);
    setMerging(true);

    try {
      await documentoApi.mergeDocumenti(selectedDocumentIds);
      setSelectedDocumentIds([]);
      fetchDocumenti(page);
    } catch (e) {
      setMergeError(
        e.response?.data?.error ||
        e.response?.data ||
        'Errore durante il merge'
      );
    } finally {
      setMerging(false);
    }
  };

  // ---------------- RENDER ----------------
  return (
    <Container sx={{ mt: 2, pb: 4 }}>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Documenti
      </Typography>

      {/* ACTION BAR */}
      <Stack direction="row" spacing={1}>
        {user.role === "CONTRIBUENTE" && (
          <ProtectedAction user={user} contractState={user.statoContratto}>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/contribuenti/${user.contribuenteId}/documenti/upload`)
              }
            >
              Upload PDF
            </Button>
          </ProtectedAction>
        )}
      </Stack>

      {/* MERGE */}
      <Stack sx={{ mt: 2 }}>
        <ProtectedAction user={user} contractState={user.statoContratto}>
          <Button
            variant="contained"
            color="warning"
            disabled={!canMerge() || merging}
            onClick={handleMerge}
          >
            Unisci documenti
          </Button>
        </ProtectedAction>
      </Stack>

      {mergeError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {mergeError}
        </Alert>
      )}

      {/* LOADING */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* LISTA */}
      <Stack spacing={2} sx={{ mt: 2 }}>
        {documenti.map(d => (
          <Accordion key={d.id} disableGutters>

            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>

                <Tooltip title="Seleziona per merge">
                  <Checkbox
                    checked={selectedDocumentIds.includes(d.id)}
                    onChange={() => toggleSelection(d.id)}
                  />
                </Tooltip>

                {/* TESTO */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                    {d.tipo} {d.numeroDocumento} {formatDate(d.dataCreazione)}
                  </Typography>

                  <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                    {truncateText(d.descrizione, 80)}
                  </Typography>
                </Box>

                {/* BADGE FIX */}
                {d.rateSaltate > 0 && (
                  <Badge
                    color="error"
                    badgeContent={d.rateSaltate}
                    sx={{
                      mr: 1,
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none'
                      }
                    }}
                  />
                )}

              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2">
                {d.descrizione}
              </Typography>

              <Typography variant="body2">
                {d.contribuente?.nome} {d.contribuente?.cognome}
              </Typography>

              <Typography variant="body2">
                Data: {formatDate(d.dataCreazione)}
              </Typography>

              <Typography variant="body2">
                Decaduto: {d.decaduta ? 'SI' : 'NO'}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => navigate(`/documenti/${d.id}`)}
                >
                  Visualizza
                </Button>

                <ProtectedAction user={user} contractState={user.statoContratto}>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/documenti/edit/${d.id}`)}
                  >
                    Modifica
                  </Button>

                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => openDeleteDialog(d.id)}
                  >
                    Elimina
                  </Button>
                </ProtectedAction>
              </Stack>
            </AccordionDetails>

          </Accordion>
        ))}
      </Stack>

      {/* PAGINAZIONE */}
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
        {Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i}
            variant={i === page ? "contained" : "outlined"}
            onClick={() => fetchDocumenti(i)}
          >
            {i + 1}
          </Button>
        ))}
      </Stack>

      {/* DELETE */}
      <ConfirmDeleteModal
        open={openDeleteModal}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        message="Sei sicuro di voler eliminare questo documento?"
      />

    </Container>
  );
}
